import { deepAnalysisApi } from "../service";
import {
    WebsiteIndexEntry,
    FetchWebsiteIndexEntryParams,
    AddWebsiteIndexEntryParams,
    UpdateWebsiteIndexEntryParams,
    UpdateWebsiteIndexEntrySettingsParams,
    DeleteWebsiteIndexEntryParams,
    FetchWebsiteIndexStatusResponse,
    FetchWebsiteIndexStatusParams,
    WebsiteIndexScheduledTasks,
    FetchWebsiteIndexScheduledTasksParams,
    OnPageSeoScore,
    FetchOnPageSeoScoreParams,
    AddWebsiteIndexEntryInBackgroundResponse,
    AddWebsiteIndexEntryInBackgroundParams,
    FetchFaviconResponse,
} from "./types";


const websiteIndexEntryApi = deepAnalysisApi.injectEndpoints({
    endpoints: (builder) => ({
        fetchWebsiteIndexEntries: builder.query<WebsiteIndexEntry[], void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/website-index',
                };
            },

            providesTags: [{ type: 'WebsiteIndexEntries', id: 'LIST' }],
        }),
        fetchFavicons: builder.query<FetchFaviconResponse[], void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/website-index/favicons',
                };
            },

            providesTags: [{ type: 'Favicons', id: 'LIST' }],
        }),
        fetchWebsiteIndexEntry: builder.query<WebsiteIndexEntry, FetchWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/${guid}`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'WebsiteIndexEntries', id: args.guid }],
        }),

        fetchWebsiteIndexStatus: builder.query<FetchWebsiteIndexStatusResponse, FetchWebsiteIndexStatusParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/setup-status/${guid}`,
                };
            },
        }),

        fetchWebsiteIndexScheduledTasks: builder.query<WebsiteIndexScheduledTasks, FetchWebsiteIndexScheduledTasksParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/scheduled-tasks/${guid}`,
                };
            },
        }),

        fetchOnPageSeoScore: builder.query<OnPageSeoScore, FetchOnPageSeoScoreParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/on-page-seo-score/${guid}`,
                };
            },
        }),

        addWebsiteIndexEntry: builder.mutation<WebsiteIndexEntry, AddWebsiteIndexEntryParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: `/website-index`,
                };
            },

            invalidatesTags: [
                { type: 'WebsiteIndexEntries', id: 'LIST' }
            ]
        }),

        addWebsiteIndexEntryInBackground: builder.mutation<AddWebsiteIndexEntryInBackgroundResponse, AddWebsiteIndexEntryInBackgroundParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/website-index/background',
                };
            },

            invalidatesTags: [{ type: 'WebsiteIndexEntries', id: 'LIST' }],
        }),

        updateWebsiteIndexEntry: builder.mutation<WebsiteIndexEntry, UpdateWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid, ...rest } = args;

                return {
                    method: 'PATCH',
                    body: rest,
                    url: `/website-index/${guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'WebsiteIndexEntries', id: 'LIST' },
                { type: 'WebsiteIndexEntries', id: args.guid },
            ],
        }),

        updateWebsiteIndexEntrySettings: builder.mutation<WebsiteIndexEntry, UpdateWebsiteIndexEntrySettingsParams>({
            query: (args) => {
                const { guid, ...rest } = args;

                return {
                    method: 'PATCH',
                    body: rest,
                    url: `/website-index/settings/${guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'WebsiteIndexEntries', id: 'LIST' },
                { type: 'WebsiteIndexEntries', id: args.guid },
            ],
        }),

        deleteWebsiteIndexEntry: builder.mutation<void, DeleteWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'DELETE',
                    url: `/website-index/${guid}`,
                };
            },

            invalidatesTags: [
                { type: 'WebsiteIndexEntries', id: 'LIST' }
            ]
        }),

        validateDomain: builder.query<{ domain: string, is_valid: boolean }, { domain: string }>({
            query: (arg) => {
                const { domain } = arg;
                return {
                    method: 'GET',
                    url: `/website-index/validate-domain?domain=${encodeURIComponent(domain)}`,
                };
            }
        }),
    }),
});

export const {
    useAddWebsiteIndexEntryMutation,
    useDeleteWebsiteIndexEntryMutation,
    useFetchWebsiteIndexEntriesQuery,
    useLazyFetchWebsiteIndexEntriesQuery,
    useFetchWebsiteIndexEntryQuery,
    useLazyFetchWebsiteIndexEntryQuery,
    useUpdateWebsiteIndexEntryMutation,
    useUpdateWebsiteIndexEntrySettingsMutation,
    useFetchWebsiteIndexStatusQuery,
    useFetchWebsiteIndexScheduledTasksQuery,
    useFetchOnPageSeoScoreQuery,
    useAddWebsiteIndexEntryInBackgroundMutation,
    useFetchFaviconsQuery,
    useLazyValidateDomainQuery
} = websiteIndexEntryApi;
